import { useMemo } from 'react';
import { usePayment } from '$api-clients/hooks';
import {
  buildReportingSummaryPerformanceQuery,
  getAllTimePeriods,
  getLast13Periods,
  getPeriodsForDateRange,
  isReportingPeriodAllTime,
  isReportingPeriodCustom,
  isReportingPeriodL365,
  shouldHidePeriodComparisons,
} from '../../helpers';
import { useAllTimeStartDate } from '../../ReportsConfiguration/useReportsConfiguration';
import { usePerformanceReportContext } from '../context/PerformanceReportContext';
import {
  accumulateValues,
  DEFAULT_DATA,
  extrapolateSalesValueGoals,
  formatPeriodMonthLabel,
} from './useGetPerformanceData.helpers';

export const useGetPerformanceData = ({
  viewReportAs,
}: {
  viewReportAs: string;
}) => {
  const { filters } = usePerformanceReportContext();
  const allTimeStartDate = useAllTimeStartDate();
  const performanceQuery = buildReportingSummaryPerformanceQuery(
    filters,
    viewReportAs,
    allTimeStartDate,
  );
  const hideComparisons = shouldHidePeriodComparisons(filters?.reportingPeriod);

  const selectedPeriods = useMemo(() => {
    const reportingPeriod = filters?.reportingPeriod;
    if (isReportingPeriodL365(reportingPeriod)) return getLast13Periods();
    if (
      isReportingPeriodCustom(reportingPeriod) &&
      filters?.customDateStart &&
      filters?.customDateEnd
    ) {
      return getPeriodsForDateRange(
        filters.customDateStart,
        filters.customDateEnd,
      );
    }
    if (isReportingPeriodAllTime(reportingPeriod))
      return getAllTimePeriods(allTimeStartDate);
    return null;
  }, [
    filters?.reportingPeriod,
    filters?.customDateStart,
    filters?.customDateEnd,
    allTimeStartDate,
  ]);

  const dynamicLimit = selectedPeriods ? selectedPeriods.length : 12;

  const { data, isLoading: isDataLoading } = usePayment('/reporting/summary', {
    params: {
      query: {
        ...performanceQuery,
        period: 'MONTHLY',
        offset: 0,
        limit: dynamicLimit,
      },
    },
  });

  // Goal data must always use CLOSED status only — salesValueGoal is an agent-level
  // goal that doesn't vary by transaction status. Using the status-filtered query
  // causes the goal line shape to change when Pending is included (more months of
  // goal data appear, altering the extrapolation).
  const { data: goalData, isLoading: isGoalDataLoading } = usePayment(
    '/reporting/summary',
    hideComparisons
      ? null
      : {
          params: {
            query: {
              ...performanceQuery,
              transactionStatus: ['CLOSED'],
              period: 'MONTHLY',
              offset: 0,
              limit: 12,
            },
          },
        },
  );

  const yearBeforeReportingPeriod =
    filters?.reportingPeriod != null &&
    typeof filters.reportingPeriod === 'number'
      ? filters.reportingPeriod - 1
      : new Date().getFullYear() - 1;
  const { data: priorYearData, isLoading: isPriorYearDataLoading } = usePayment(
    '/reporting/summary',
    hideComparisons
      ? null
      : {
          params: {
            query: {
              ...performanceQuery,
              closingDateStart: `${yearBeforeReportingPeriod}-01-01`,
              closingDateEnd: `${yearBeforeReportingPeriod}-12-31`,
              period: 'MONTHLY',
              offset: 0,
              limit: 12,
            },
          },
        },
  );

  const rawResults = data?.results ?? [];

  const selectedPeriodRows = useMemo(() => {
    if (!selectedPeriods) return { forAccumulation: [], forMonthlyTotals: [] };
    const baseRows = selectedPeriods.map(({ year, month }) => {
      const row = rawResults.find(
        (r) =>
          (r as { periodYear?: number }).periodYear === year &&
          r.periodMonth === month,
      );
      return {
        periodMonth: month,
        adjustedGrossCommission: row?.adjustedGrossCommission ?? null,
        salesVolume: row?.salesVolume ?? null,
        salesValueGoal: row?.salesValueGoal ?? null,
      };
    });
    return {
      forAccumulation: baseRows,
      forMonthlyTotals: baseRows.map(
        ({ periodMonth, adjustedGrossCommission, salesVolume }) => ({
          periodMonth,
          adjustedGrossCommission,
          salesVolume,
        }),
      ),
    };
  }, [selectedPeriods, rawResults]);

  const accumulationSource = selectedPeriods
    ? selectedPeriodRows.forAccumulation
    : rawResults;
  const accumulatedValues = accumulateValues(accumulationSource);
  const priorYearAccumulatedValues = accumulateValues(
    priorYearData?.results ?? [],
  );
  const accumulatedGoalValues = accumulateValues(goalData?.results ?? []);
  const salesValueGoalData = hideComparisons
    ? []
    : extrapolateSalesValueGoals(accumulatedGoalValues);

  const results = useMemo(() => {
    if (selectedPeriods) {
      return selectedPeriods.map(({ year, month }, i) => {
        const row = accumulatedValues[i];
        const monthlyRow = selectedPeriodRows.forMonthlyTotals[i];
        return {
          adjustedGrossCommission: row?.adjustedGrossCommission ?? null,
          salesValueGoal: null,
          salesVolume: row?.salesVolume ?? null,
          monthlyAdjustedGrossCommission:
            monthlyRow?.adjustedGrossCommission ?? null,
          monthlySalesVolume: monthlyRow?.salesVolume ?? null,
          monthlySalesValueGoal: null,
          priorYearSalesVolume: undefined,
          priorYearAdjustedGrossCommission: undefined,
          monthlyPriorYearSalesVolume: undefined,
          monthlyPriorYearAdjustedGrossCommission: undefined,
          month: formatPeriodMonthLabel(year, month),
        };
      });
    }

    return DEFAULT_DATA.map(
      (
        {
          month,
          salesValueGoal: defaultSalesValueGoal,
          adjustedGrossCommission: defaultAdjustedGrossCommission,
          salesVolume: defaultSalesVolume,
        },
        indx,
      ) => {
        const periodMonth = indx + 1;
        const rawValueForMonth = rawResults.find(
          (value) => value.periodMonth === periodMonth,
        );
        const accumulatedValueForMonth = accumulatedValues.find(
          (value) => value.periodMonth === periodMonth,
        );

        const priorYearRawValueForMonth = (priorYearData?.results ?? []).find(
          (value) => value.periodMonth === periodMonth,
        );
        const priorYearAccumulatedValuesForMonth =
          priorYearAccumulatedValues.find(
            (value) => value.periodMonth === periodMonth,
          );

        const salesValueGoalForMonth = salesValueGoalData.findLast(
          (value) => value.periodMonth === periodMonth,
        )?.salesValueGoal;

        const salesValueGoalForPrevMonth =
          periodMonth > 1
            ? (salesValueGoalData.findLast(
                (value) => value.periodMonth === periodMonth - 1,
              )?.salesValueGoal ?? 0)
            : 0;

        const monthlySalesValueGoal =
          salesValueGoalForMonth != null
            ? salesValueGoalForMonth - salesValueGoalForPrevMonth
            : null;

        return {
          adjustedGrossCommission:
            accumulatedValueForMonth?.adjustedGrossCommission ??
            defaultAdjustedGrossCommission,
          salesValueGoal: salesValueGoalForMonth ?? defaultSalesValueGoal,
          salesVolume:
            accumulatedValueForMonth?.salesVolume ?? defaultSalesVolume,
          monthlyAdjustedGrossCommission:
            rawValueForMonth?.adjustedGrossCommission ??
            defaultAdjustedGrossCommission,
          monthlySalesVolume:
            rawValueForMonth?.salesVolume ?? defaultSalesVolume,
          monthlySalesValueGoal,
          priorYearSalesVolume: priorYearAccumulatedValuesForMonth?.salesVolume,
          priorYearAdjustedGrossCommission:
            priorYearAccumulatedValuesForMonth?.adjustedGrossCommission,
          monthlyPriorYearSalesVolume: priorYearRawValueForMonth?.salesVolume,
          monthlyPriorYearAdjustedGrossCommission:
            priorYearRawValueForMonth?.adjustedGrossCommission,
          month,
        };
      },
    );
  }, [
    selectedPeriods,
    selectedPeriodRows,
    accumulatedValues,
    rawResults,
    priorYearData?.results,
    salesValueGoalData,
    priorYearAccumulatedValues,
  ]);

  return {
    isLoading: isDataLoading || isPriorYearDataLoading || isGoalDataLoading,
    results,
  };
};
